// Limites do push de sync que o SERVIDOR impõe por item — espelhados aqui pra o flusher
// não mandar o que seria recusado com certeza (ver "QUARENTENA" em outbox.ts).
//
// Fonte da verdade: `SwipeSyncItem` em apps/api/src/routes/schemas.ts. O teste
// push-limits.test.ts lê aquele arquivo e fica VERMELHO se os números divergirem: o
// oráculo é o arquivo do servidor, que este módulo não move.
//
// REGRA DE OURO DO ESPELHO: nunca mais estrito que o servidor. Um item que o servidor
// aceitaria e o cliente recusa é dado do usuário parado em quarentena por engano. Por
// isso só os dois limites mensuráveis sem ambiguidade entram (tamanho do payload e do
// itemKey); formato de data, enum de kind e o resto ficam com o servidor, e o que ele
// recusar cai na bisseção do flusher, que isola o item pela resposta dele.

/** `MAX_PAYLOAD_BYTES` do servidor: o payload serializado em JSON, em bytes UTF-8. */
export const SERVER_MAX_PAYLOAD_BYTES = 16 * 1024
/** `itemKey: z.string().min(1).max(200)` do servidor (unidades UTF-16, como `.length`). */
export const SERVER_MAX_ITEM_KEY_LENGTH = 200

/** Motivo da recusa local (vai pra marca da quarentena e pra telemetria). */
export type LocalRejection = 'payload_too_large' | 'item_key_invalid'

/**
 * Bytes UTF-8 de uma string, igual ao `Buffer.byteLength(s, 'utf8')` do servidor —
 * inclusive no surrogate solto, que o Node codifica como U+FFFD (3 bytes). Sem
 * `TextEncoder` de propósito: é uma conta, e roda igual em todo motor do fleet.
 */
export function utf8ByteLength(s: string): number {
  let bytes = 0
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    if (c < 0x80) bytes += 1
    else if (c < 0x800) bytes += 2
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const next = s.charCodeAt(i + 1)
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4
        i += 1
      } else {
        bytes += 3
      }
    } else bytes += 3
  }
  return bytes
}

/**
 * O servidor recusaria este item com certeza? Devolve o motivo, ou `null` quando o item
 * pode ir (o que não quer dizer que o servidor vá aceitar: ver "REGRA DE OURO").
 */
export function localRejection(item: { itemKey: string; payload: unknown }): LocalRejection | null {
  if (item.itemKey.length < 1 || item.itemKey.length > SERVER_MAX_ITEM_KEY_LENGTH) return 'item_key_invalid'
  if (item.payload !== undefined) {
    let json: string | undefined
    try {
      json = JSON.stringify(item.payload)
    } catch {
      // Não serializável (não acontece com valor vindo do IDB, que é clone estruturado):
      // o cliente não sabe medir, então não recusa. O servidor decide.
      return null
    }
    if (utf8ByteLength(json ?? '') > SERVER_MAX_PAYLOAD_BYTES) return 'payload_too_large'
  }
  return null
}
